import React, { useState, useRef, useEffect } from 'react';
import { Button, Modal, Input, Alert, Select, Space, Typography, Divider } from 'antd';
import { CodeOutlined, ExperimentOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

/**
 * Expression Tester Component
 *
 * Provides an interface to test JavaScript expressions against call data
 * with autocomplete suggestions and live evaluation.
 *
 * @param {Object} props
 * @param {Array} props.calls - Array of call objects to test against
 */
const ExpressionTester = ({ calls = [], onCreateCustomField }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [expression, setExpression] = useState('record.');
    const [selectedCall, setSelectedCall] = useState(null);
    const [evaluationResult, setEvaluationResult] = useState({ value: null, error: null });
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [cursorPosition, setCursorPosition] = useState(0);
    const [highlightedSuggestion, setHighlightedSuggestion] = useState(0);
    const inputRef = useRef(null);
    const suggestionsRef = useRef(null);

    // Initialize with a random call when modal opens
    useEffect(() => {
        if (isModalOpen && calls.length > 0 && !selectedCall) {
            const randomIndex = Math.floor(Math.random() * calls.length);
            setSelectedCall(calls[randomIndex]);
        }
    }, [isModalOpen, calls, selectedCall]);

    // Evaluate the expression when it changes or the selected call changes
    useEffect(() => {
        if (!selectedCall) return;

        try {
            // Using Function constructor to evaluate the expression with the record context
            const evalFn = new Function('record', `return ${expression}`);
            const result = evalFn(selectedCall);
            setEvaluationResult({
                value: result,
                error: null
            });
        } catch (error) {
            setEvaluationResult({
                value: null,
                error: error.message
            });
        }
    }, [expression, selectedCall]);

    // Generate auto-complete suggestions based on the current expression
    useEffect(() => {
        if (!selectedCall || !isModalOpen) return;

        // Only show suggestions when typing after 'record.'
        if (!expression.includes('record.')) {
            setSuggestions([]);
            return;
        }

        const cursorPos = cursorPosition;
        const textBeforeCursor = expression.substring(0, cursorPos);

        // Check if we're in a position to show suggestions (after 'record.' but before next operation)
        const match = textBeforeCursor.match(/record\.([a-zA-Z0-9_]*)$/);
        if (!match) {
            setShowSuggestions(false);
            return;
        }

        const prefix = match[1] || '';

        // Reset the highlighted suggestion index when suggestions change
        setHighlightedSuggestion(0);

        // Recursively get all possible paths in the record object
        const getPaths = (obj, currentPath = '') => {
            if (!obj || typeof obj !== 'object') return [];

            let paths = [];
            for (const key in obj) {
                if (Object.prototype.hasOwnProperty.call(obj, key)) {
                    const newPath = currentPath ? `${currentPath}.${key}` : key;

                    // Add this path
                    paths.push({
                        path: newPath,
                        type: typeof obj[key],
                        value: obj[key]
                    });

                    // Recursively add nested paths
                    if (obj[key] && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
                        paths = [...paths, ...getPaths(obj[key], newPath)];
                    }
                }
            }
            return paths;
        };

        // Start from 'record' and get all paths
        const allPaths = getPaths(selectedCall);

        // Filter paths based on the current prefix
        const filteredSuggestions = allPaths
            .filter(item => item.path.startsWith(prefix))
            .map(item => {
                const displayPath = item.path;
                let displayValue = '';

                // Format the display value based on type
                if (item.type === 'string') {
                    displayValue = `"${String(item.value).substring(0, 20)}${String(item.value).length > 20 ? '...' : ''}"`;
                } else if (item.type === 'number' || item.type === 'boolean') {
                    displayValue = String(item.value);
                } else if (item.type === 'object') {
                    displayValue = Array.isArray(item.value) ? 'Array' : 'Object';
                } else if (item.value === null) {
                    displayValue = 'null';
                } else if (item.value === undefined) {
                    displayValue = 'undefined';
                }

                return {
                    path: displayPath,
                    fullPath: `record.${displayPath}`,
                    type: item.type,
                    displayValue
                };
            });

        setSuggestions(filteredSuggestions);
        setShowSuggestions(filteredSuggestions.length > 0);
    }, [expression, selectedCall, cursorPosition, isModalOpen]);

    const handleExpressionChange = (e) => {
        setExpression(e.target.value);
        setCursorPosition(e.target.selectionStart);
    };

    const handleCursorPositionChange = (e) => {
        setCursorPosition(e.target.selectionStart);
    };

    const handleCallChange = (callId) => {
        const selectedCall = calls.find(call => call.id === callId);
        if (selectedCall) {
            setSelectedCall(selectedCall);
        }
    };

    const handleSuggestionSelect = (fullPath) => {
        // Replace the text from 'record.' + current path with the selected suggestion
        const cursorPos = cursorPosition;
        const textBeforeCursor = expression.substring(0, cursorPos);
        const match = textBeforeCursor.match(/record\.([a-zA-Z0-9_.]*)$/);

        if (match) {
            const startPos = cursorPos - match[1].length;
            const newExpression =
                expression.substring(0, startPos - 7) + // -7 to remove 'record.'
                fullPath +
                expression.substring(cursorPos);

            setExpression(newExpression);
            setShowSuggestions(false);

            // Focus back on input and place cursor after the inserted suggestion
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus();
                    // Safely set selection only on input elements (not textarea)
                    try {
                        const newCursorPos = startPos - 7 + fullPath.length;
                        // Check if setSelectionRange is available (it is on input elements)
                        if (typeof inputRef.current.setSelectionRange === 'function') {
                            inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
                        } else {
                            // For textarea elements, we need a different approach
                            const textarea = inputRef.current.resizableTextArea.textArea;
                            textarea.setSelectionRange(newCursorPos, newCursorPos);
                        }
                        setCursorPosition(newCursorPos);
                    } catch (err) {
                        console.log('Could not set selection range:', err);
                    }
                }
            }, 0);
        }
    };

    // Format the result for display
    const formatResultValue = (value) => {
        if (value === null) return 'null';
        if (value === undefined) return 'undefined';

        if (typeof value === 'object') {
            try {
                return JSON.stringify(value, null, 2);
            } catch (e) {
                return String(value);
            }
        }

        if (typeof value === 'string') {
            return `"${value}"`;
        }

        return String(value);
    };

    // Determine if the result value is a color
    const isColorValue = (value) => {
        if (typeof value !== 'string') return false;

        // Check if value is a hex color
        if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value)) return true;

        // Check if value is an rgb/rgba color
        if (/^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/.test(value)) return true;
        if (/^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*[\d.]+\s*\)$/.test(value)) return true;

        // Check if value is a named color
        const namedColors = [
            'red', 'green', 'blue', 'yellow', 'purple', 'orange', 'black',
            'white', 'gray', 'pink', 'brown', 'cyan', 'magenta'
        ];
        return namedColors.includes(value.toLowerCase());
    };

    return (
        <>
            <Button
                type="primary"
                icon={<ExperimentOutlined />}
                onClick={() => setIsModalOpen(true)}
                style={{ marginRight: 8 }}
            >
                Expression Tester
            </Button>

            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <CodeOutlined style={{ marginRight: 8 }} />
                        <span>Expression Tester</span>
                    </div>
                }
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                width={800}
                footer={[
                    <Button key="close" onClick={() => setIsModalOpen(false)}>
                        Close
                    </Button>,
                    <Button key="clear" onClick={() => setExpression('record.')}>
                        Clear
                    </Button>,
                    <Button
                        key="createField"
                        type="primary"
                        onClick={() => {
                            setIsModalOpen(false);
                            onCreateCustomField && onCreateCustomField(expression);
                        }}
                    >
                        Create Custom Field
                    </Button>

                ]}
            >
                {selectedCall ? (
                    <div>
                        <div style={{ marginBottom: 16 }}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Text strong>Test your JavaScript expressions with real call data</Text>

                                    <Select
                                        style={{ width: 200 }}
                                        showSearch
                                        value={selectedCall.id}
                                        onChange={handleCallChange}
                                        placeholder="Select different call"
                                    >
                                        {calls.map(call => (
                                            <Option key={call.id} value={call.id}>
                                                {call.agentName || call.id}
                                            </Option>
                                        ))}
                                    </Select>
                                </div>

                                <Alert
                                    message="Expression Context"
                                    description="Use 'record' to access the call data. Example: record.qualScore > 80 ? '#22c55e' : '#ef4444'"
                                    type="info"
                                    showIcon
                                />
                            </Space>
                        </div>

                        <div style={{ position: 'relative' }}>
                            <TextArea
                                ref={inputRef}
                                value={expression}
                                onChange={handleExpressionChange}
                                onKeyUp={(e) => {
                                    handleCursorPositionChange(e);
                                }}
                                onKeyDown={(e) => {
                                    // Handle keyboard navigation for suggestions
                                    if (showSuggestions && suggestions.length > 0) {
                                        // Tab or Enter to autocomplete the highlighted suggestion
                                        if ((e.key === 'Tab' || e.key === 'Enter') && e.target.selectionStart === e.target.selectionEnd) {
                                            e.preventDefault();
                                            handleSuggestionSelect(suggestions[highlightedSuggestion].fullPath);
                                            return;
                                        }

                                        // Arrow down to navigate to next suggestion
                                        if (e.key === 'ArrowDown') {
                                            e.preventDefault();
                                            setHighlightedSuggestion((prev) =>
                                                prev < suggestions.length - 1 ? prev + 1 : prev
                                            );
                                            return;
                                        }

                                        // Arrow up to navigate to previous suggestion
                                        if (e.key === 'ArrowUp') {
                                            e.preventDefault();
                                            setHighlightedSuggestion((prev) => prev > 0 ? prev - 1 : 0);
                                            return;
                                        }

                                        // Escape to close suggestions
                                        if (e.key === 'Escape') {
                                            e.preventDefault();
                                            setShowSuggestions(false);
                                            return;
                                        }
                                    }
                                }}
                                onClick={handleCursorPositionChange}
                                placeholder="Enter your expression here... (Use Tab to complete suggestions)"
                                autoSize={{ minRows: 3, maxRows: 6 }}
                                style={{
                                    fontFamily: 'monospace',
                                    fontSize: '14px',
                                    marginBottom: 8
                                }}
                            />

                            {showSuggestions && (
                                <div
                                    ref={suggestionsRef}
                                    style={{
                                        position: 'absolute',
                                        zIndex: 1000,
                                        background: 'white',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: '2px',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                        maxHeight: '200px',
                                        overflow: 'auto',
                                        width: '100%'
                                    }}
                                >
                                    {suggestions.map((suggestion, index) => (
                                        <div
                                            key={index}
                                            onClick={() => handleSuggestionSelect(suggestion.fullPath)}
                                            style={{
                                                padding: '8px 12px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                borderBottom: index === suggestions.length - 1 ? 'none' : '1px solid #f0f0f0',
                                                background: highlightedSuggestion === index
                                                    ? '#e6f7ff'
                                                    : index % 2 === 0 ? '#fafafa' : 'white',
                                                borderLeft: highlightedSuggestion === index ? '3px solid #1890ff' : 'none',
                                            }}
                                            onMouseEnter={() => setHighlightedSuggestion(index)}
                                        >
                      <span style={{
                          fontWeight: highlightedSuggestion === index ? 'bold' : 'normal',
                          paddingLeft: highlightedSuggestion === index ? '0' : '3px',
                      }}>
                        {suggestion.path}
                      </span>
                                            <span style={{
                                                color: '#888',
                                                marginLeft: 16,
                                                fontStyle: 'italic',
                                                fontSize: '12px'
                                            }}>
                        {suggestion.displayValue} <Text type="secondary">({suggestion.type})</Text>
                      </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <Divider orientation="left">Evaluation Result</Divider>

                        {evaluationResult.error ? (
                            <Alert
                                message="Error"
                                description={evaluationResult.error}
                                type="error"
                                showIcon
                            />
                        ) : (
                            <div>
                                <div style={{
                                    padding: '12px',
                                    background: '#f9f9f9',
                                    borderRadius: '4px',
                                    fontFamily: 'monospace'
                                }}>
                                    {evaluationResult.value !== undefined && (
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <Text strong>Value: </Text>
                                                <pre style={{ marginLeft: 8, marginBottom: 0 }}>
                          {formatResultValue(evaluationResult.value)}
                        </pre>
                                            </div>

                                            <div style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
                                                <Text strong>Type: </Text>
                                                <Text code style={{ marginLeft: 8 }}>
                                                    {typeof evaluationResult.value}
                                                </Text>
                                            </div>

                                            {isColorValue(evaluationResult.value) && (
                                                <div style={{ marginTop: 8 }}>
                                                    <Text strong>Color Preview:</Text>
                                                    <div
                                                        style={{
                                                            marginTop: 4,
                                                            width: '100%',
                                                            height: '30px',
                                                            background: evaluationResult.value,
                                                            borderRadius: '4px',
                                                            border: '1px solid #d9d9d9'
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <Divider orientation="left">Available Properties</Divider>

                        <div style={{
                            maxHeight: '200px',
                            overflow: 'auto',
                            border: '1px solid #f0f0f0',
                            borderRadius: '4px',
                            padding: '8px'
                        }}>
              <pre style={{ fontSize: '12px' }}>
                {JSON.stringify(selectedCall, null, 2)}
              </pre>
                        </div>
                    </div>
                ) : (
                    <Alert
                        message="No call data available"
                        description="Please make sure there are calls loaded in the system."
                        type="warning"
                        showIcon
                    />
                )}
            </Modal>
        </>
    );
};

export default ExpressionTester;
